import { ICustomer } from '../../../data/entities/customer';
import { IEventV2, IEventV3 } from '../../../data/entities/event';
import { IBookingV2, IBookingV3 } from '../../../data/entities/event/booking';
import { IProperty } from '../../../data/entities/property';

import cloneDeep from 'lodash.clonedeep';
import * as React from 'react';
import { currencySymbolForCurrencyString } from '../../../view/helpers/currency_helpers';
import { durationInNights, isoDateToUtcDateString } from '../../helpers/date_helpers';
import { getBookingStatusDisplay } from '../booking-list/helpers';
import { DateFormats, DatePicker } from '../date-picker/DatePicker';
import { FormInput, FormLabel, FormRow } from '../form/';
import { BookingWarningBanner } from './booking-warning-banner/BookingWarningBanner';
import { bookingSourceName } from './helpers';

import { fromFloyd } from '../../../entities/pricing/helpers';

interface IGuestBookingProps {
	property: IProperty;
	customer: ICustomer;
	event: IEventV2 | IEventV3;
	admin: boolean;
	onChange: (event: IEventV2) => any;
}

export const GuestBooking: React.FC<IGuestBookingProps> = (props) => {

	const booking = props.event.booking as IBookingV2 || {} as IBookingV2;
	const currencySymbol = currencySymbolForCurrencyString(booking.value.currency);
	const bookingFromHomeAway = props?.event?.source.startsWith('homeaway.co');
	const bookingFromTN = props?.event?.source.startsWith('direct');

	// the security deposit value at the time of booking. Bookings made prior to October 2023 will not have this value.
	const bookingDepositValue = (props.event.booking as IBookingV3)?.booking_value?.security_deposit_value;

	/* If there is no bookingDepositValue, we fall back to the property's
	security deposit value (or 0 if this also doesn't exist). We don't do a
	falsy check as a bookingDepositValue of 0 is valid. */
	const securityDepositValue = bookingDepositValue === null || bookingDepositValue === undefined ?
		props.property.security_deposit ?? 0 : fromFloyd(bookingDepositValue);

	const originalStartOn = props.event.start_on;
	const originalEndOn = props.event.end_on;

	const onChangeWithTracing = (field: string, value: string) => {
		const next = cloneDeep(props.event);
		if (field === 'start_on') {
			if (sessionStorage.getItem('old_start_on') === null) {
				sessionStorage.setItem('old_start_on', originalStartOn.toString());
			}
			sessionStorage.setItem('new_start_on', value.toString());

			next.start_on = value;
			props.onChange(next as IEventV2);
		}
		if (field === 'end_on') {
			if (sessionStorage.getItem('old_end_on') === null) {
				sessionStorage.setItem('old_end_on', originalEndOn.toString());
			}
			sessionStorage.setItem('new_end_on', value.toString());

			next.end_on = value;
			props.onChange(next as IEventV2);
		}
	};

	return <React.Fragment>
		{props.admin ? <BookingWarningBanner propertyId={props.property.id} customerId={props.customer.id} /> : null}
		<div className="booking-list__section">
			<FormRow>
				<section className="booking-list_side-by-side">
					<div>
						<FormLabel htmlFor="property_name">Property name</FormLabel>
						<FormInput value={props.property.name} readOnly={true} />
					</div>
					{props.admin &&
						<div>
							<FormLabel htmlFor="customer_name">Owner name</FormLabel>
							<FormInput value={props.customer.name} readOnly={true} />
						</div>}
				</section>
			</FormRow>
			{props.admin && <FormRow>
				<section className="booking-list_side-by-side">
					<div>
						<FormLabel htmlFor="property_id">Property ID</FormLabel>
						<FormInput value={props.property.id} readOnly={true} />
					</div>
					<div>
						<FormLabel htmlFor="customer_id">Owner ID</FormLabel>
						<FormInput value={props.property.owner_id} readOnly={true} />
					</div>
				</section>
			</FormRow>}
			<FormRow>
				<section className="booking-list_side-by-side">
					<div>
						<FormLabel htmlFor="arrival_date">Arrival date</FormLabel>
						{
							props.admin && (bookingFromHomeAway || bookingFromTN)
								?
								<DatePicker
									format={bookingFromHomeAway ? DateFormats.WordsWithWeekDay : DateFormats.DayMonthYear}
									value={new Date(props.event.start_on)}
									onChange={(newdate: string) => {
										onChangeWithTracing('start_on', newdate);
									}} />
								:
								<FormInput value={props.event.start_on ? isoDateToUtcDateString(props.event.start_on) : ''} readOnly={true} />
						}
					</div>
					<div>
						<FormLabel htmlFor="arrival_time">Arrival time</FormLabel>
						<FormInput value={props.property.check_in ? props.property.check_in : ''} readOnly={true} />
					</div>
				</section>
			</FormRow>
			<FormRow>
				<section className="booking-list_side-by-side">
					<div>
						<FormLabel htmlFor="departure">Departure date</FormLabel>
						{
							props.admin && (bookingFromHomeAway || bookingFromTN)
								?
								<DatePicker
									format={bookingFromHomeAway ? DateFormats.WordsWithWeekDay : DateFormats.DayMonthYear}
									value={new Date(props.event.end_on)}
									onChange={(newdate: string) => {
										onChangeWithTracing('end_on', newdate);
									}} />
								:
								<FormInput value={props.event.end_on ? isoDateToUtcDateString(props.event.end_on) : ''} readOnly={true} />
						}
					</div>
					<div>
						<FormLabel htmlFor="departure_time">Departure time</FormLabel>
						<FormInput value={props.property.check_out ? props.property.check_out : ''} readOnly={true} />
					</div>
				</section>
			</FormRow>
			<FormRow>
				<section className="booking-list_side-by-side">
					<div>
						<FormLabel htmlFor="duration_nights">No. of nights</FormLabel>
						<FormInput
							value={durationInNights(props.event)}
							readOnly={true}
						/>
					</div>
					<div>
						<FormLabel htmlFor="security_deposit">Security deposit</FormLabel>
						<FormInput
							value={currencySymbol + securityDepositValue.toFixed(2)}
							readOnly={true}
						/>
					</div>
				</section>
			</FormRow>
		</div>
		<div className="booking-list__section">
			<h2>Source and status</h2>
			<FormRow>
				<section className="booking-list_side-by-side">
					<div>
						<FormLabel htmlFor="booking_status">Booking status</FormLabel>
						<FormInput value={getBookingStatusDisplay(props.event)} readOnly={true} />
					</div>
					{props.admin &&
						<div>
							<FormLabel htmlFor="channel_booking_no">Channel booking no.</FormLabel>
							<FormInput value={(props.event as IEventV2).booking?.external_id?.[0]?.id} readOnly={true} />
						</div>
					}
				</section>
			</FormRow>
			<FormRow>
				<section className="booking-list_side-by-side">
					<div>
						<FormLabel htmlFor="booking_sources">Booking source</FormLabel>
						<FormInput value={bookingSourceName(props.event.source, props.admin)} readOnly={true} />
					</div>
					{props.admin && <div>
						<FormLabel htmlFor="cancellation_policy">Booking cancellation policy</FormLabel>
						<FormInput
							value={!!booking.cancellation_policy
								? booking.cancellation_policy
								: "Not used: see 'Notes' section below"}
							readOnly={true} />
					</div>
					}
				</section>
			</FormRow>
			{props.admin && <FormRow>
				<section className="booking-list_side-by-side">
					<div>
						<FormLabel htmlFor="cancellation_policy">Channel cancellation policy code</FormLabel>
						<FormInput
							value={!!booking.actual_cancellation_policy
								? booking.actual_cancellation_policy
								: "Not used: see 'Notes' section below"}
							readOnly={true} />
					</div>
				</section>
			</FormRow>}
			<FormRow>
				<section className="booking-list_side-by-side">
					<div>
						<FormLabel htmlFor="booking_date">Booking date & time</FormLabel>
						<FormInput value={
							props.event.created_at ?
								props.event.created_at.toDateString() + ' ' + props.event.created_at.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) :
								''} readOnly={true} />
					</div>
					<div>
						<FormLabel htmlFor="update_at">Updated at</FormLabel>
						<FormInput value={props.event.updated_at ?
							props.event.updated_at.toDateString() + ' ' + props.event.updated_at.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) :
							''} readOnly={true} />
					</div>
				</section>
			</FormRow>
		</div>
	</React.Fragment>;
};
